/**
 * API Key 管理页面
 * API Key Management Page
 *
 * 功能：
 * - 查询 API Key 列表（分页、筛选）
 * - 创建 API Key（统一接口，支持房间级/场馆级，三级联动选择）
 * - 查看 API Key 详情
 * - 撤销 API Key
 * - 查看使用日志
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Descriptions,
  Typography,
  Alert,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  KeyOutlined,
  DeleteOutlined,
  EyeOutlined,
  HistoryOutlined,
  ReloadOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { RadioChangeEvent } from 'antd/es/radio';
import dayjs from 'dayjs';
import { merchantApi, merchantVenueApi, merchantRoomApi } from '../services/api';
import apiKeyService, { getAPIKeyList } from '../services/apiKeyService';
import type {
  APIKey,
  CreateAPIKeyRequest,
  CreateAPIKeyResponse,
  UsageLog,
} from '../types/apiKey';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// 商户、场馆、房间类型定义
interface Merchant {
  id: number;
  company_name: string;
  status: string;
}

interface Venue {
  id: number;
  venue_name: string;
  merchant_id: number;
  status: string;
}

interface Room {
  id: number;
  room_name: string;
  venue_id: number;
  status: string;
}

const ApiKeysManagement: React.FC = () => {
  // ============================================================
  // 状态管理
  // ============================================================

  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 筛选条件
  const [keyTypeFilter, setKeyTypeFilter] = useState<'room' | 'venue' | undefined>();
  const [statusFilter, setStatusFilter] = useState<'active' | 'revoked' | undefined>();

  // 创建 API Key 模态框
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [keyLevelType, setKeyLevelType] = useState<'room' | 'venue'>('room');

  // 三级联动数据
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);

  // 详情模态框
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null);

  // 日志模态框
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);

  // ============================================================
  // 数据加载
  // ============================================================

  const loadApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAPIKeyList({
        page,
        limit: pageSize,
        key_type: keyTypeFilter,
        status: statusFilter,
      });
      setApiKeys(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '加载 API Key 列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyTypeFilter, statusFilter]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  // ============================================================
  // 三级联动数据加载
  // ============================================================

  // 加载商户列表
  const loadMerchants = useCallback(async () => {
    setMerchantsLoading(true);
    try {
      const result = await merchantApi.getMerchants({ page: 1, page_size: 100 });
      setMerchants(result.data.items);
    } catch (error: any) {
      message.error(error.message || '加载商户列表失败');
    } finally {
      setMerchantsLoading(false);
    }
  }, []);

  // 加载场馆列表
  const loadVenues = useCallback(async (merchantId: number) => {
    setVenuesLoading(true);
    try {
      const result = await merchantVenueApi.getVenues({ merchant_id: merchantId, page: 1, page_size: 100 });
      setVenues(result.data.items);
    } catch (error: any) {
      message.error(error.message || '加载场馆列表失败');
    } finally {
      setVenuesLoading(false);
    }
  }, []);

  // 加载房间列表
  const loadRooms = useCallback(async (merchantId: number, venueId: number) => {
    setRoomsLoading(true);
    try {
      const result = await merchantRoomApi.getRoomsByVenue(venueId, { page: 1, page_size: 100 });
      setRooms(result.data.items);
    } catch (error: any) {
      message.error(error.message || '加载房间列表失败');
    } finally {
      setRoomsLoading(false);
    }
  }, []);

  // 模态框打开时加载商户列表
  useEffect(() => {
    if (createModalVisible) {
      loadMerchants();
    }
  }, [createModalVisible, loadMerchants]);

  // ============================================================
  // 三级联动处理
  // ============================================================

  // 商户选择变化
  const handleMerchantChange = (merchantId: number) => {
    // 清空下游数据
    createForm.setFieldsValue({ venue_id: undefined, room_id: undefined });
    setVenues([]);
    setRooms([]);

    if (merchantId) {
      loadVenues(merchantId);
    }
  };

  // 场馆选择变化
  const handleVenueChange = (venueId: number) => {
    // 清空房间数据
    createForm.setFieldsValue({ room_id: undefined });
    setRooms([]);

    const merchantId = createForm.getFieldValue('merchant_id');
    if (merchantId && venueId && keyLevelType === 'room') {
      loadRooms(merchantId, venueId);
    }
  };

  // API Key 类型切换
  const handleKeyTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value;
    setKeyLevelType(newType);

    // 切换类型时清空房间相关字段
    if (newType === 'venue') {
      createForm.setFieldsValue({ room_id: undefined });
      setRooms([]);
    }
  };

  // ============================================================
  // 创建 API Key
  // ============================================================

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();

      // 构建统一的请求
      const request: CreateAPIKeyRequest = {
        name: values.name,
        venue_id: values.venue_id,
        permissions: values.permissions || ['device:*'],
        expires_at: values.expires_at?.toISOString(),
        description: values.description,
      };

      // 房间级 Key 需要 room_id
      if (keyLevelType === 'room') {
        request.room_id = values.room_id;
      }

      console.log('Creating API Key with request:', request);

      const result = await apiKeyService.createAPIKey(request);

      message.success('API Key 创建成功');

      // 显示完整 API Key（仅此一次）
      Modal.success({
        title: 'API Key 创建成功',
        content: (
          <div>
            <Alert
              message="重要提示"
              description="请立即复制并保存您的 API Key。出于安全原因，完整内容仅在创建时显示一次。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Paragraph>
              <Text strong>API Key:</Text>
            </Paragraph>
            <Paragraph copyable={{ text: result.api_key }}>
              <Text code style={{ fontSize: 16, wordBreak: 'break-all' }}>
                {result.api_key}
              </Text>
            </Paragraph>
          </div>
        ),
        okText: '已保存',
        onOk: () => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setVenues([]);
          setRooms([]);
          setKeyLevelType('room');
          loadApiKeys();
        },
      });
    } catch (error: any) {
      console.error('Create API Key error:', error);

      // 提取详细错误信息
      let errorMsg = '创建 API Key 失败';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.data?.errors?.[0]) {
          const fieldErrors = data.data.errors.map((e: any) =>
            `${e.loc?.join('.') || ''}: ${e.msg}`.trim()
          ).join('; ');
          errorMsg = `${data.message} - ${fieldErrors}`;
        } else if (data.message) {
          errorMsg = data.message;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      message.error(errorMsg);
    }
  };

  // ============================================================
  // 撤销 API Key
  // ============================================================

  const handleRevoke = async (keyId: string, name: string) => {
    try {
      await apiKeyService.revokeAPIKey(keyId);
      message.success(`API Key "${name}" 已撤销`);
      loadApiKeys();
    } catch (error: any) {
      message.error(error.message || '撤销 API Key 失败');
    }
  };

  // ============================================================
  // 查看详情
  // ============================================================

  const handleViewDetail = (record: APIKey) => {
    setSelectedKey(record);
    setDetailModalVisible(true);
  };

  // ============================================================
  // 查看使用日志
  // ============================================================

  const handleViewLogs = async (record: APIKey) => {
    setSelectedKey(record);
    setLogsModalVisible(true);
    setLogsPage(1);
    await loadUsageLogs(record.key_id, 1);
  };

  const loadUsageLogs = async (keyId: string, page: number) => {
    setLogsLoading(true);
    try {
      const result = await apiKeyService.getAPIKeyUsageLogs(keyId, {
        page,
        limit: 10,
      });
      setUsageLogs(result.items);
      setLogsTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '加载使用日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  // ============================================================
  // 表格列定义
  // ============================================================

  const columns: ColumnsType<APIKey> = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      width: 400,
      render: (apiKey: string) => (
        <Space>
          <Text code copyable={{ text: apiKey }} style={{ fontSize: 12 }}>
            {apiKey.substring(0, 30)}...{apiKey.substring(apiKey.length - 20)}
          </Text>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'key_type',
      key: 'key_type',
      width: 100,
      render: (type: 'room' | 'venue') => (
        <Tag color={type === 'room' ? 'blue' : 'green'}>
          {type === 'room' ? '房间级' : '场馆级'}
        </Tag>
      ),
    },
    {
      title: '绑定范围',
      key: 'scope',
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          {record.key_type === 'room' ? (
            <Tooltip title={`房间 ID: ${record.room_id}`}>
              <Tag icon={<HomeOutlined />}>房间 {record.room_id}</Tag>
            </Tooltip>
          ) : (
            <Tooltip title={`场馆 ID: ${record.venue_id}`}>
              <Tag icon={<HomeOutlined />}>场馆 {record.venue_id}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 150,
      render: (permissions: string[] | undefined, record: APIKey) => {
        // 如果没有 permissions 字段，根据 key_type 显示默认权限
        const displayPermissions = permissions || (record.key_type === 'room' ? ['device:*', 'game:control', 'room:read'] : ['device:*', 'game:*', 'room:*']);
        return (
          <Space size={4} wrap>
            {displayPermissions.map((perm) => (
              <Tag key={perm} color="purple">{perm}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: 'active' | 'revoked') => (
        <Tag color={status === 'active' ? 'success' : 'error'}>
          {status === 'active' ? '活跃' : '已撤销'}
        </Tag>
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      render: (count: number) => <Text>{count}</Text>,
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 160,
      render: (date: string | null) => (
        <Text type="secondary">
          {date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '从未使用'}
        </Text>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 120,
      render: (date: string | null) => (
        <Text type={date && dayjs(date).isBefore(dayjs()) ? 'danger' : 'secondary'}>
          {date ? dayjs(date).format('YYYY-MM-DD') : '永不过期'}
        </Text>
      ),
    },
    {
      title: '创建者',
      dataIndex: 'created_by_username',
      key: 'created_by_username',
      width: 120,
      render: (name: string) => <Text>{name || `User${name}`}</Text>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => <Text type="secondary">{dayjs(date).format('YYYY-MM-DD HH:mm')}</Text>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="使用日志">
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleViewLogs(record)}
            />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title="撤销 API Key"
              description={`确定要撤销 API Key "${record.name}" 吗？撤销后将无法恢复。`}
              onConfirm={() => handleRevoke(record.key_id, record.name)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                撤销
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ============================================================
  // 日志表格列定义
  // ============================================================

  const logColumns: ColumnsType<UsageLog> = [
    {
      title: '操作时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作类型',
      dataIndex: 'operation_type',
      key: 'operation_type',
      width: 150,
    },
    {
      title: '目标类型',
      dataIndex: 'target_type',
      key: 'target_type',
      width: 100,
    },
    {
      title: '目标 ID',
      dataIndex: 'target_id',
      key: 'target_id',
      width: 120,
      ellipsis: true,
    },
    {
      title: '操作者',
      dataIndex: 'admin_name',
      key: 'admin_name',
      width: 120,
    },
  ];

  // ============================================================
  // 渲染
  // ============================================================

  return (
    <div>
      <Card
        title={
          <Space>
            <KeyOutlined />
            <span>API Key 管理</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              style={{ width: 120 }}
              placeholder="类型筛选"
              allowClear
              value={keyTypeFilter}
              onChange={setKeyTypeFilter}
            >
              <Select.Option value="room">房间级</Select.Option>
              <Select.Option value="venue">场馆级</Select.Option>
            </Select>
            <Select
              style={{ width: 120 }}
              placeholder="状态筛选"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Select.Option value="active">活跃</Select.Option>
              <Select.Option value="revoked">已撤销</Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={loadApiKeys}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
              创建 API Key
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="key_id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size || 20);
            },
          }}
        />
      </Card>

      {/* 创建 API Key 模态框 - 统一接口 + 三级联动 */}
      <Modal
        title="创建 API Key"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setVenues([]);
          setRooms([]);
          setKeyLevelType('room');
        }}
        onOk={handleCreate}
        width={600}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="API Key 名称"
            rules={[{ required: true, message: '请输入 API Key 名称' }]}
          >
            <Input placeholder="例如：本地服务器-房间001" />
          </Form.Item>

          {/* API Key 类型选择 */}
          <Form.Item label="API Key 类型">
            <Radio.Group value={keyLevelType} onChange={handleKeyTypeChange}>
              <Radio value="room">房间级 API Key（绑定单个房间）</Radio>
              <Radio value="venue">场馆级 API Key（绑定整个场馆）</Radio>
            </Radio.Group>
          </Form.Item>

          {/* 三级联动选择：商户 → 场馆 → 房间 */}
          <Form.Item
            name="merchant_id"
            label="商户"
            rules={[{ required: true, message: '请选择商户' }]}
          >
            <Select
              placeholder="请选择商户"
              loading={merchantsLoading}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleMerchantChange}
              options={merchants.map((m) => ({
                value: m.id,
                label: `[${m.id}] ${m.company_name}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="venue_id"
            label="场馆"
            rules={[{ required: true, message: '请选择场馆' }]}
          >
            <Select
              placeholder="请先选择商户"
              loading={venuesLoading}
              disabled={!createForm.getFieldValue('merchant_id')}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleVenueChange}
              options={venues.map((v) => ({
                value: v.id,
                label: `[${v.id}] ${v.venue_name}`,
              }))}
            />
          </Form.Item>

          {keyLevelType === 'room' && (
            <Form.Item
              name="room_id"
              label="房间"
              rules={[{ required: true, message: '请选择房间' }]}
            >
              <Select
                placeholder="请先选择场馆"
                loading={roomsLoading}
                disabled={!createForm.getFieldValue('venue_id')}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={rooms.map((r) => ({
                  value: r.id,
                  label: `[${r.id}] ${r.room_name}`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="permissions"
            label="权限"
            rules={[{ required: true, message: '请选择权限' }]}
            initialValue={['device:*']}
          >
            <Select mode="tags" placeholder="选择权限">
              <Select.Option value="device:*">设备控制 (全部)</Select.Option>
              <Select.Option value="device:read">设备查询</Select.Option>
              <Select.Option value="device:control">设备控制</Select.Option>
              <Select.Option value="device:function:*">函数调用</Select.Option>
              <Select.Option value="world:read">世界查询</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="expires_at" label="过期时间">
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="选择过期时间（可选）"
            />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="请输入描述信息（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="API Key 详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {selectedKey && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="名称">{selectedKey.name}</Descriptions.Item>
            <Descriptions.Item label="类型">
              <Tag color={selectedKey.key_type === 'room' ? 'blue' : 'green'}>
                {selectedKey.key_type === 'room' ? '房间级' : '场馆级'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Key ID">{selectedKey.key_id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedKey.status === 'active' ? 'success' : 'error'}>
                {selectedKey.status === 'active' ? '活跃' : '已撤销'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="房间 ID">
              {selectedKey.room_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="场馆 ID">
              {selectedKey.venue_id}
            </Descriptions.Item>
            <Descriptions.Item label="商户 ID">
              {selectedKey.merchant_id}
            </Descriptions.Item>
            <Descriptions.Item label="使用次数">
              {selectedKey.usage_count}
            </Descriptions.Item>
            <Descriptions.Item label="权限" span={2}>
              <Space size={4} wrap>
                {selectedKey.permissions && selectedKey.permissions.length > 0
                  ? selectedKey.permissions.map((perm) => (
                      <Tag key={perm} color="purple">{perm}</Tag>
                    ))
                  : selectedKey.key_type === 'room'
                    ? <Tag>device:*, game:control, room:read</Tag>
                    : <Tag>device:*, game:*, room:*</Tag>
                }
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="最后使用">
              {selectedKey.last_used_at
                ? dayjs(selectedKey.last_used_at).format('YYYY-MM-DD HH:mm:ss')
                : '从未使用'}
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {selectedKey.expires_at
                ? dayjs(selectedKey.expires_at).format('YYYY-MM-DD HH:mm:ss')
                : '永不过期'}
            </Descriptions.Item>
            <Descriptions.Item label="创建者">
              {selectedKey.created_by_username || `User${selectedKey.created_by}`}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(selectedKey.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            {selectedKey.description && (
              <Descriptions.Item label="描述" span={2}>
                {selectedKey.description}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 使用日志模态框 */}
      <Modal
        title={`使用日志 - ${selectedKey?.name}`}
        open={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLogsModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        <Table
          columns={logColumns}
          dataSource={usageLogs}
          rowKey="id"
          loading={logsLoading}
          pagination={{
            current: logsPage,
            pageSize: 10,
            total: logsTotal,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => {
              setLogsPage(p);
              if (selectedKey) {
                loadUsageLogs(selectedKey.key_id, p);
              }
            },
          }}
        />
      </Modal>
    </div>
  );
};

export default ApiKeysManagement;
